import { useState } from "react";
import { Button, Container, Paper, Typography } from "@mui/material";
import { CheckCircle, Share } from "@mui/icons-material";
import { green } from "@mui/material/colors";
import { makeStyles } from "@/compat/muiStyles";
import { useHistory } from "@/compat/router";
import moduleAreas, { moduleUrls } from "../../constants/moduleAreaConstants";
import { UPLOAD_RESULT_KEY } from "../../constants/riskAssessmentUploadConstants";
import { baseRoute } from "../../routes";

type Created = { id?: number; name: string };

const useStyles = makeStyles((theme) => ({
  paper: {
    maxWidth: 620,
    margin: "0 auto",
    padding: theme.spacing(6, 4),
    textAlign: "center",
  },
  check: {
    fontSize: 56,
    color: green[500],
    marginBottom: theme.spacing(2),
  },
  subtitle: {
    color: theme.palette.text.secondary,
    margin: `${theme.spacing(1)} auto ${theme.spacing(4)}`,
    maxWidth: 440,
  },
  actions: {
    display: "flex",
    gap: theme.spacing(2),
    justifyContent: "center",
    flexWrap: "wrap",
  },
  back: {
    marginTop: theme.spacing(3),
  },
}));

export default function UploadRiskAssessmentComplete() {
  const classes = useStyles();
  const history = useHistory();

  const [created] = useState<Created[]>(() => {
    try {
      const raw = sessionStorage.getItem(UPLOAD_RESULT_KEY);
      return raw ? (JSON.parse(raw) as Created[]) : [];
    } catch {
      return [];
    }
  });

  const raBase = `${baseRoute}/${moduleUrls[moduleAreas.RISKASSESSMENTS]}`;
  const single = created.length === 1 ? created[0] : null;

  // Consume the handoff on leaving so a later visit (without a fresh upload) can't reuse a stale RA.
  const goTo = (path: string) => {
    sessionStorage.removeItem(UPLOAD_RESULT_KEY);
    history.push(path);
  };

  const heading = single
    ? `${single.name} uploaded`
    : created.length > 1
      ? `${created.length} risk assessments uploaded`
      : "Risk assessment uploaded";

  return (
    <Container>
      <Paper className={classes.paper}>
        <CheckCircle className={classes.check} />
        <Typography variant="h5" gutterBottom>
          {heading}
        </Typography>
        <Typography variant="body1" className={classes.subtitle}>
          Saved to the register. It can now be downloaded, shared for acknowledgement, and reviewed by uploading a new
          version.
        </Typography>

        <div className={classes.actions}>
          {single?.id ? (
            <>
              <Button variant="contained" color="primary" onClick={() => goTo(`${raBase}/view/${single.id}`)}>
                View Assessment
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<Share />}
                onClick={() => goTo(`${raBase}/view/${single.id}?share=1`)}
              >
                Share with Others
              </Button>
            </>
          ) : (
            <Button variant="contained" color="primary" onClick={() => goTo(raBase)}>
              Go to Risk Assessments
            </Button>
          )}
        </div>

        <Button className={classes.back} color="primary" onClick={() => goTo(raBase)}>
          Back to Risk Assessments list
        </Button>
      </Paper>
    </Container>
  );
}
